import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ScanLine, Upload } from 'lucide-react';
import { toast } from 'sonner';

import {
  processReceipt, listProcessedReceipts, getProcessedReceipt,
  updateProcessedReceipt, deleteProcessedReceipt,
} from '@/lib/api';
import { FurnitureNavBar } from '@/components/furniture/FurnitureNavBar';
import { ReceiptAllocationView } from '@/components/furniture/ReceiptAllocationView';
import { ClarifyQuantityView } from '@/components/furniture/ClarifyQuantityView';
import { UploadCard } from '@/components/furniture/receipt/UploadCard';
import { RecentList } from '@/components/furniture/receipt/RecentList';
import { ReceiptReviewPanel } from '@/components/furniture/receipt/ReceiptReviewPanel';

export default function ReceiptProcessingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [receipts, setReceipts] = useState([]);
  const [active, setActive] = useState(null);      // active receipt being edited
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editable, setEditable] = useState({
    vendor: '', total_detected: '', note: '', items: [],
    tax: '', shipping: '', shipping_mode: 'proportional', shipping_item_id: null,
  });
  // Which step the user is on for an active receipt:
  // 'clarify_qty' — disambiguate multi-qty AI lines (per-unit vs combined)
  // 'review'      — edit the parsed items + totals
  // 'allocate'    — link line items to furniture items
  const [stage, setStage] = useState('review');

  const loadList = async () => {
    try {
      const r = await listProcessedReceipts();
      setReceipts(r.receipts || []);
      return r.receipts || [];
    } catch { return []; }
  };

  useEffect(() => {
    const initialId = searchParams.get('id');
    (async () => {
      const list = await loadList();
      if (initialId) {
        try {
          const one = await getProcessedReceipt(initialId);
          openReceipt(one);
        } catch { /* ignored */ }
      } else {
        // Auto-pick the most recent in-progress receipt so the user lands
        // back where they left off. If everything's already saved, stay on
        // the upload screen so they can start a new one.
        const inProgress = (list || []).find(r => r.status !== 'committed');
        if (inProgress) openReceipt(inProgress);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openReceipt = (r) => {
    setActive(r);
    // Items with qty>1 that haven't been human-confirmed yet get routed
    // through the clarification step first. qty<=1, already-clarified lines,
    // and anything on an already-committed receipt skip straight to review.
    const items = (r.items || []).map(it => ({ ...it }));
    const needsClarify = r.status !== 'committed' && items.some(
      it => Number(it.quantity) > 1 && !it.qty_clarified,
    );
    setStage(needsClarify ? 'clarify_qty' : 'review');
    setEditable({
      vendor: r.vendor || '',
      total_detected: r.total_detected == null ? '' : String(r.total_detected),
      note: r.note || '',
      items,
      tax: r.tax == null ? '' : String(r.tax),
      shipping: r.shipping == null ? '' : String(r.shipping),
      shipping_mode: r.shipping_mode || 'proportional',
      shipping_item_id: r.shipping_item_id || null,
    });
    const params = new URLSearchParams(searchParams);
    params.set('id', r.id);
    setSearchParams(params, { replace: true });
  };

  const closeReceipt = () => {
    setActive(null);
    const params = new URLSearchParams(searchParams);
    params.delete('id');
    setSearchParams(params, { replace: true });
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const r = await processReceipt(file);
      toast.success(`Parsed ${r.items?.length || 0} line item${r.items?.length === 1 ? '' : 's'}`);
      await loadList();
      openReceipt(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to process receipt');
    } finally {
      setUploading(false);
    }
  };

  // Called from the clarify-qty step. `mode` is 'keep' or 'split'.
  // The AI's `detected_price` is the raw number printed on the receipt for
  // that line, untouched by any math. The user picks how to interpret it:
  //   keep  → apply detected to each unit: unit = detected, total = qty × detected
  //   split → split detected across all units: unit = detected/qty, total = detected
  const answerQtyClarification = (id, mode) => setEditable(prev => ({
    ...prev,
    items: prev.items.map(it => {
      if (it.id !== id) return it;
      const qty = Number(it.quantity) || 1;
      const detected = Number(
        it.detected_price != null ? it.detected_price : it.unit_price,
      ) || 0;
      if (mode === 'keep') {
        return {
          ...it,
          detected_price: detected,
          unit_price: detected,
          line_total: +(qty * detected).toFixed(2),
          qty_clarified: true,
        };
      }
      return {
        ...it,
        detected_price: detected,
        unit_price: qty > 0 ? +(detected / qty).toFixed(2) : detected,
        line_total: detected,
        qty_clarified: true,
      };
    }),
  }));

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const updated = await updateProcessedReceipt(active.id, {
        vendor: editable.vendor,
        note: editable.note,
        total_detected: editable.total_detected === '' ? null : editable.total_detected,
        tax: editable.tax === '' ? 0 : editable.tax,
        shipping: editable.shipping === '' ? 0 : editable.shipping,
        shipping_mode: editable.shipping_mode,
        shipping_item_id: editable.shipping_mode === 'assigned' ? editable.shipping_item_id : null,
        items: editable.items,
      });
      toast.success('Changes saved');
      setActive(updated);
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteProcessedReceipt(id);
      if (active?.id === id) closeReceipt();
      await loadList();
      toast.success('Receipt deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="min-h-screen bg-[#fbfaf7]" data-testid="receipt-processing-page">
      <FurnitureNavBar />
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/furniture-projects')} data-testid="back-to-projects">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#1f2933] flex items-center gap-2">
              <ScanLine className="h-6 w-6 text-[#2d948c]" /> Receipt Processing
            </h1>
            <p className="text-sm text-[#5b6773] mt-0.5">
              Upload a receipt — Claude Sonnet reads it and returns editable line items.
              {active && " You'll be able to match items to furniture projects in the next step."}
            </p>
          </div>
          {active && (
            <Button variant="outline" onClick={closeReceipt} data-testid="upload-another-btn">
              <Upload className="h-4 w-4 mr-1.5" /> Upload another
            </Button>
          )}
        </div>

        {!active ? (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
            <UploadCard onUpload={handleUpload} uploading={uploading} />
            <RecentList receipts={receipts} onOpen={openReceipt} onDelete={handleDelete} />
          </div>
        ) : stage === 'clarify_qty' ? (
          <ClarifyQuantityView
            receipt={active}
            items={editable.items}
            onAnswer={answerQtyClarification}
            onBack={closeReceipt}
            onContinue={async () => { await save(); setStage('review'); }}
            saving={saving}
          />
        ) : stage === 'allocate' ? (
          <ReceiptAllocationView
            receipt={{ ...active, items: editable.items }}
            onBack={() => setStage('review')}
            onSaved={async () => {
              // Receipt is committed — push it to the "Saved receipts"
              // list and reset the workspace to the upload screen so the
              // user can immediately drop in the next one.
              await loadList();
              closeReceipt();
            }}
          />
        ) : (
          <ReceiptReviewPanel
            active={active}
            editable={editable}
            setEditable={setEditable}
            saving={saving}
            onSave={save}
            onDelete={handleDelete}
            onNextAllocate={async () => { await save(); setStage('allocate'); }}
          />
        )}
      </div>
    </div>
  );
}
